import React from 'react';
import PropTypes from 'prop-types';

/**
 * FileUploadSection - File upload with PDF processing mode selector
 * 
 * Three modes for PDF processing:
 * - local: Extract text locally in browser (default, works everywhere)
 * - native: Send to API for native processing (OpenAI/Anthropic/OpenRouter only)
 * - images: Convert to images for vision models (requires vision-capable model)
 */
function FileUploadSection({
  llmProvider,
  pdfProcessingMode = 'local',
  setPdfProcessingMode,
  imageQuality = 'medium',
  setImageQuality,
  hasPdfFiles = false,
  supportsVision = false,
  uploadedFiles,
  fileContents,
  onFileUpload,
  onRemoveFile,
  formatFileSize
}) {
  const supportsNativeUpload = ['openai', 'anthropic', 'openrouter'].includes(llmProvider);
  
  // Processing mode options
  const processingModes = [
    {
      id: 'local',
      label: 'Lokal parsen',
      description: 'Text wird im Browser extrahiert',
      available: true,
      color: 'primary'
    },
    {
      id: 'native',
      label: 'API senden',
      description: 'Datei wird an API übertragen',
      available: supportsNativeUpload,
      color: 'success'
    },
    {
      id: 'images',
      label: 'Als Bilder',
      description: 'PDF-Seiten als Bilder (Vision)',
      available: supportsVision,
      color: 'secondary'
    }
  ];

  return (
    <div className="space-y-4">
      {/* PDF Processing Mode Selector - Only show when PDFs are uploaded */}
      {hasPdfFiles && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            PDF-Verarbeitung:
          </div>
          
          {/* Triple Switch */}
          <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
            {processingModes.map((mode) => {
              const isSelected = pdfProcessingMode === mode.id;
              const isDisabled = !mode.available;
              
              return (
                <button
                  key={mode.id}
                  onClick={() => mode.available && setPdfProcessingMode(mode.id)}
                  disabled={isDisabled}
                  className={`
                    flex-1 px-3 py-2 text-sm font-medium transition-all
                    ${isSelected 
                      ? `bg-${mode.color}-600 text-white` 
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                    ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                    ${mode.id !== 'local' ? 'border-l border-gray-300 dark:border-gray-600' : ''}
                  `}
                  title={!mode.available ? `Nicht verfügbar für ${llmProvider}` : mode.description}
                >
                  {mode.label}
                </button>
              );
            })}
          </div>
          
          {/* Mode Description */}
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {pdfProcessingMode === 'local' && (
              'Text wird lokal im Browser extrahiert. Günstig, funktioniert überall.'
            )}
            {pdfProcessingMode === 'native' && (
              'PDF wird direkt an die API gesendet. Bessere Formatierung, höhere Kosten.'
            )}
            {pdfProcessingMode === 'images' && (
              'PDF-Seiten werden als Bilder an das Vision-Modell gesendet. Ideal für Diagramme/Grafiken.'
            )}
          </div>
          
          {/* Warnings */}
          {pdfProcessingMode === 'native' && (
            <div className="mt-2 p-2 bg-warning-50 dark:bg-warning-900/30 border border-warning-200 dark:border-warning-700 rounded text-xs text-warning-800 dark:text-warning-300">
              Höhere Token-Kosten (~3x). Nur für Dokumente mit wichtigen visuellen Elementen empfohlen.
            </div>
          )}
          {pdfProcessingMode === 'images' && (
            <>
              <div className="mt-2 p-2 bg-info-50 dark:bg-info-900/30 border border-info-200 dark:border-info-700 rounded text-xs text-info-800 dark:text-info-300">
                Vision-Modus: Jede Seite wird als Bild verarbeitet. Ideal für Diagramme, Tabellen, Formeln.
              </div>
              
              {/* Image Quality Selector */}
              {setImageQuality && (
                <div className="mt-3 flex items-center gap-3">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Bildqualität:</span>
                  <div className="flex rounded overflow-hidden border border-gray-300 dark:border-gray-600">
                    {['low', 'medium', 'high'].map((quality) => (
                      <button
                        key={quality}
                        onClick={() => setImageQuality(quality)}
                        className={`
                          px-3 py-1 text-xs font-medium theme-transition
                          ${imageQuality === quality 
                            ? 'bg-secondary-600 text-white' 
                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }
                          ${quality !== 'low' ? 'border-l border-gray-300 dark:border-gray-600' : ''}
                        `}
                      >
                        {quality === 'low' ? 'Niedrig' : quality === 'medium' ? 'Mittel' : 'Hoch'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      {/* File Input */}
      <div>
        <label className="block w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/30 theme-transition">
          <span className="text-primary-600 dark:text-primary-400 font-medium">+ Dateien auswählen</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">max. 10MB pro Datei</span>
          <input
            type="file"
            multiple
            onChange={onFileUpload}
            className="hidden"
            accept=".txt,.md,.pdf,.doc,.docx,.json,.py,.js,.jsx,.ts,.tsx,.java,.cpp,.c,.cs,.rb,.go,.rs,.swift,.kt,.php,.html,.css,.scss,.xml,.yaml,.yml,.sql,.sh"
          />
        </label>
      </div>

      {/* File List */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          {uploadedFiles.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate block">{file.name}</span>
                {fileContents[index] && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(fileContents[index].size)} · {fileContents[index].type.toUpperCase()}
                  </span>
                )}
              </div>
              <button
                onClick={() => onRemoveFile(index)}
                className="ml-3 text-danger-600 dark:text-danger-400 hover:text-danger-800 dark:hover:text-danger-300 text-sm font-medium"
              >
                Entfernen
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Info text when no files uploaded */}
      {uploadedFiles.length === 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Unterstützt: PDF, DOCX, TXT, MD, Code-Dateien
        </p>
      )}
    </div>
  );
}

FileUploadSection.propTypes = {
  llmProvider: PropTypes.string.isRequired,
  pdfProcessingMode: PropTypes.oneOf(['local', 'native', 'images']),
  setPdfProcessingMode: PropTypes.func,
  imageQuality: PropTypes.oneOf(['low', 'medium', 'high']),
  setImageQuality: PropTypes.func,
  hasPdfFiles: PropTypes.bool,
  supportsVision: PropTypes.bool,
  uploadedFiles: PropTypes.array.isRequired,
  fileContents: PropTypes.array.isRequired,
  onFileUpload: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  formatFileSize: PropTypes.func.isRequired
};

export default FileUploadSection;
